import { useState, useMemo } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { ChevronsUpDown, Plus, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { useCustomers } from "@/hooks/useCustomers";
import { NewCustomerDialog } from "./NewCustomerDialog";

interface Props {
  value: string;
  onValueChange: (id: string) => void;
  placeholder?: string;
}

export const CustomerSelectWithCreate = ({ value, onValueChange, placeholder = "Select customer" }: Props) => {
  const { data: customers } = useCustomers();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogInitialName, setDialogInitialName] = useState("");

  const selectedName = useMemo(() => {
    return customers?.find((c) => c.id === value)?.name || "";
  }, [customers, value]);

  const filtered = useMemo(() => {
    if (!customers) return [];
    if (!search.trim()) return customers;
    return customers.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()));
  }, [customers, search]);

  const exactMatch = useMemo(() => {
    if (!search.trim() || !customers) return true;
    return customers.some((c) => c.name.toLowerCase() === search.toLowerCase());
  }, [customers, search]);

  const handleCreateClick = () => {
    setDialogInitialName(search);
    setOpen(false);
    setDialogOpen(true);
  };

  const handleCreated = (customer: { id: string; name: string }) => {
    onValueChange(customer.id);
    setSearch("");
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between font-normal">
            {selectedName || <span className="text-muted-foreground">{placeholder}</span>}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput placeholder="Search customers…" value={search} onValueChange={setSearch} />
            <CommandList>
              <CommandEmpty className="py-2 px-3 text-sm text-muted-foreground">No customers found.</CommandEmpty>
              <CommandGroup>
                {filtered.map((c) => (
                  <CommandItem key={c.id} value={c.id} onSelect={() => { onValueChange(c.id); setOpen(false); setSearch(""); }}>
                    <Check className={cn("mr-2 h-4 w-4", value === c.id ? "opacity-100" : "opacity-0")} />
                    {c.name}
                  </CommandItem>
                ))}
              </CommandGroup>
              {search.trim() && !exactMatch && (
                <CommandGroup>
                  <CommandItem onSelect={handleCreateClick} className="text-primary">
                    <Plus className="mr-2 h-4 w-4" />
                    Create &quot;{search}&quot;
                  </CommandItem>
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <NewCustomerDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initialName={dialogInitialName}
        onCreated={handleCreated}
      />
    </>
  );
};
